import { Tag, Document, User, Custom, System, P, S, M, psm,  Package, CompoundDocument } from "./types/Types";
import { getDocumentList } from "./DocumentsFuncs";

/**
 * Get the package list from rwapi
 * @returns {Package[]} package list
 */
export async function getPackageList(prov: string, accId: string, sessionId: string): Promise<Package[]> {
    const res = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/docmasters/${prov}/${accId}/packagelist?SessionID=${sessionId}`)
        .then((res) => {
            if (res.status === 200 && res.ok) {
                return res;
            }
            throw new Error("Network response was not ok.");
        });

    const data = await res.json();
    if (data.ok) {
        let pkgList: Package[] = [];
        let docList: Document[] = [];
        try {
            docList = (await getDocumentList(prov, accId, sessionId)).docList;
        } catch (e) {
            throw new Error("getPackageList: Unable to get document list: " + e);
        }
        data.result.pkgInfoList.forEach((pkgInfo) => {
            pkgList.push(constructPackage(pkgInfo, docList));
        });
        return pkgList;
    } else {
        throw new Error(data.error);
    }
}

/**
 * Update the package list to rwapi
 * @param {Package[]} pkgList package list to update
 */
export async function updatePackageList(prov: string, accId: string, sessionId: string, pkgList: Package[]): Promise<boolean> {
    if (!pkgList || pkgList.length === 0)
        throw new Error("Unable to update empty or null package list");

    let pkgInfoList = [];
    pkgList.forEach((pkg) => {
        pkgInfoList.push(constructPkgInfo(pkg));
    });

    const res = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/docmasters/${prov}/${accId}/updatepackagelist?SessionID=${sessionId}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({"pkgInfoList": pkgInfoList}),
    }).then((res) => {
        if (res.status === 200 && res.ok) {
            return res;
        }
        throw new Error("Network response was not ok.");
    });

    const data = await res.json();
    if (data.ok) {
        return true;
    } else {
        throw new Error(data.error);
    }
}

export async function deletePackageList(prov: string, accId: string, sessionId: string): Promise<boolean> {
    const res = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/docmasters/${prov}/${accId}/deletepackagelist?SessionID=${sessionId}`, {
        method: 'POST',
    }).then((res) => {
        if (res.status === 200 && res.ok) {
            return res;
        }
        throw new Error("Network response was not ok.");
    });

    const data = await res.json();
    if (data.ok) {
        return true;
    } else {
        throw new Error(data.error);
    }
}

export async function deactivatePackageList(prov: string, accId: string, sessionId: string): Promise<boolean> {
    const res = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/docmasters/${prov}/${accId}/deactivatepackagelist?SessionID=${sessionId}`, {
        method: 'POST',
    }).then((res) => {
        if (res.status === 200 && res.ok) {
            return res;
        }
        throw new Error("Network response was not ok.");
    });

    const data = await res.json();
    if (data.ok) {
        return true;
    } else {
        throw new Error(data.error);
    }
}

/* Convert json to Package Object */
function constructPackage(pkgInfo, docList, prtPkgInfo?): Package {
    let pkg: Package = {
        name: pkgInfo.name,
        parentNames: prtPkgInfo? [prtPkgInfo.name]: [],
        children: [],
        doc_list: [],
        tags: []
    };

    if (prtPkgInfo?.tags?.some((tag) => psm.includes(tag))) {
        pkg.tags.push(prtPkgInfo.tags.find((tag) => psm.includes(tag)));     // inherit parent's PSM if exists
    } else if (pkgInfo.deal_type) {
        switch (pkgInfo.deal_type) {
            case "P":
                pkg.tags.push(P);
                break;
            case "S":
                pkg.tags.push(S);
                break;
            case "M":
                pkg.tags.push(M);
                break;
        }
    }

    if (pkgInfo.doc_type_list) {
        pkgInfo.doc_type_list.forEach((docTypeInfo) => {
            let doc = getDocByTypeId(docTypeInfo.doc_type_id, docList);
            if (doc) {
                doc.pkg_condition = docTypeInfo.condition ? docTypeInfo.condition.toString() : '';
                if (!docTypeInfo.is_page_reset && !doc.tags?.some((tag) => tag.name === "NoPageReset")) {
                    doc.tags.push({name: "NoPageReset"} as Tag);
                }
                pkg.doc_list.push(doc);
            }
        });
    }
    if (pkgInfo.children) {
        pkgInfo.children.forEach((childPkgInfo) => {
            pkg.children.push(constructPackage(childPkgInfo, docList, pkg));
        });
    }

    return pkg;
}

/* Convert Package Object to json */
function constructPkgInfo(pkg: Package) {
    let pkgInfo = {
        name: pkg.name,
        deal_type : "",
        doc_type_list: [],
        children: [],
    };
    if (pkg.parentNames?.length === 0 && pkg.tags.some((tag) => psm.includes(tag))) {
        let psmTag = pkg.tags.find((tag) => psm.includes(tag)).name;
        switch (psmTag) {
            case "Purchase":
                pkgInfo.deal_type = "P";
                break;
            case "Sale":
                pkgInfo.deal_type = "S";
                break;
            case "Mortgage":
                pkgInfo.deal_type = "M";
                break;
        }
    }
    if (pkg.doc_list) {
        pkgInfo.doc_type_list = pkg.doc_list.map((doc) => {
            return {
                doc_type_id: doc.doc_type_id,
                condition: doc.pkg_condition?? '',
                is_page_reset: doc.tags?.some((tag) => tag.name === "NoPageReset")? false: true,
            };
        });
    }
    if (pkg.children) {
        pkg.children.forEach((childPkg) => {
            pkgInfo.children.push(constructPkgInfo(childPkg));
        });
    }

    return pkgInfo;
}

function getDocByTypeId(doc_type_id: number, docList: Document[]): Document {
    const doc = docList.find(doc => doc.doc_type_id === doc_type_id);

    return doc? cloneDocObj(doc): null;
}

/* Create a deep copy and clone document object */
function cloneDocObj(doc: Document): Document {
    if (!doc) return null;

    doc = structuredClone(doc);
    const docCloned: Document = {
        ...doc,
        tags: doc.tags?.map((tag) => {
            if (Object.keys(tag).length === 1) {
                switch (tag.name) {
                    case "Purchase":
                        return P;
                    case "Sale":
                        return S;
                    case "Mortgage":
                        return M;
                    case "User":
                        return User;
                    case "System":
                        return System;
                    case "Custom":
                        return Custom;
                    case "Compound Document":
                        return CompoundDocument;
                    default:
                        return tag;
                }
            } else {    // complex tag object
                switch (tag.name) {
                    default:
                        return tag;
                }
            }
        }) ?? [],
    };

    return docCloned;
}